import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer, Table, Button, Space, Input, Modal, Form,
    message, Typography, Tag
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    ExclamationCircleOutlined,
    StopOutlined,
    PhoneOutlined
} from '@ant-design/icons';
import { blacklistService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const VIEW_STATES = {
    LIST: 'list',
    CREATE: 'create',
    EDIT: 'edit'
};

const BlacklistDrawer = ({ visible, onClose }) => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const canEdit = permissions.includes('callcenter.campaigns.blacklist.edit');

    const [viewState, setViewState] = useState(VIEW_STATES.LIST);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const fetchBlacklist = useCallback(async (search = null, page = 1) => {
        setLoading(true);
        try {
            const response = await blacklistService.list({
                search,
                page,
                per_page: pagination.pageSize
            });
            if (response.status === 'success') {
                setData(response.data);
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    total: response.pagination.total_records
                }));
            }
        } catch (error) {
            message.error('Error al cargar la blacklist');
        } finally {
            setLoading(false);
        }
    }, [pagination.pageSize]);

    useEffect(() => {
        if (visible) {
            fetchBlacklist();
            setViewState(VIEW_STATES.LIST);
            setSelectedEntry(null);
            form.resetFields();
        }
    }, [visible, fetchBlacklist, form]);

    const handleCreate = () => {
        form.resetFields();
        setSelectedEntry(null);
        setViewState(VIEW_STATES.CREATE);
    };

    const handleEdit = (record) => {
        setSelectedEntry(record);
        form.setFieldsValue({
            phone: record.phone,
            reason: record.reason
        });
        setViewState(VIEW_STATES.EDIT);
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '¿Eliminar de la lista negra?',
            icon: <ExclamationCircleOutlined />,
            content: `¿Está seguro de eliminar el número "${record.phone}" de la blacklist?`,
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await blacklistService.delete(record.id);
                    message.success('Número eliminado de la blacklist');
                    fetchBlacklist(null, pagination.current);
                } catch (error) {
                    message.error(error.response?.data?.message || 'Error al eliminar');
                }
            }
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (viewState === VIEW_STATES.CREATE) {
                await blacklistService.create(values);
                message.success('Número añadido correctamente');
            } else {
                await blacklistService.update(selectedEntry.id, values);
                message.success('Registro actualizado correctamente');
            }

            setViewState(VIEW_STATES.LIST);
            fetchBlacklist();
        } catch (error) {
            if (error.errorFields) return;
            message.error(error.response?.data?.message || 'Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setViewState(VIEW_STATES.LIST);
        form.resetFields();
    };

    const columns = [
        {
            title: 'Teléfono',
            dataIndex: 'phone',
            key: 'phone',
            render: (text) => <Text strong><PhoneOutlined /> {text}</Text>
        },
        {
            title: 'Motivo',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
            render: (text) => <Text type="secondary">{text || '-'}</Text>
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 100,
            render: (_, record) => canEdit && (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    />
                </Space>
            )
        }
    ];

    const renderListView = () => (
        <>
            <div style={{ marginBottom: 16 }}>
                <Input.Search
                    placeholder="Buscar por teléfono..."
                    onSearch={(value) => fetchBlacklist(value, 1)}
                    allowClear
                    style={{ width: '100%' }}
                />
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{
                    ...pagination,
                    onChange: (page) => fetchBlacklist(null, page),
                    showSizeChanger: false,
                    size: 'small'
                }}
                locale={{ emptyText: 'No hay números en la lista negra' }}
            />
        </>
    );

    const renderFormView = () => (
        <Form
            form={form}
            layout="vertical"
            style={{ marginTop: 16 }}
        >
            <Form.Item
                label="Número de Teléfono"
                name="phone"
                rules={[
                    { required: true, message: 'Ingrese el número telefónico' },
                    { pattern: /^[1-9]\d{10,14}$/, message: 'Formato inválido (E.164 sin +, min 11 dígitos, no empezar con 0)' }
                ]}
            >
                <Input placeholder="Ej. 51999888777" />
            </Form.Item>
            <Form.Item
                label="Motivo del Bloqueo"
                name="reason"
                rules={[{ max: 500, message: 'Máximo 500 caracteres' }]}
            >
                <Input.TextArea
                    placeholder="Ej. Solicitud del cliente, Fraude, etc."
                    rows={4}
                    showCount
                    maxLength={500}
                />
            </Form.Item>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
                <Button onClick={handleCancel}>
                    Cancelar
                </Button>
                <Button type="primary" onClick={handleSubmit} loading={loading}>
                    {viewState === VIEW_STATES.CREATE ? 'Añadir a Blacklist' : 'Guardar Cambios'}
                </Button>
            </div>
        </Form>
    );

    const getDrawerTitle = () => {
        const icon = <StopOutlined style={{ color: '#ff4d4f' }} />;
        switch (viewState) {
            case VIEW_STATES.CREATE:
                return (
                    <Space>
                        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleCancel} />
                        <span>Nuevo Bloqueo</span>
                    </Space>
                );
            case VIEW_STATES.EDIT:
                return (
                    <Space>
                        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleCancel} />
                        <span>Editar Bloqueo</span>
                    </Space>
                );
            default:
                return <Space>{icon} Gestión de Blacklist</Space>;
        }
    };

    return (
        <Drawer
            title={getDrawerTitle()}
            placement="right"
            width={500}
            onClose={onClose}
            open={visible}
            extra={
                viewState === VIEW_STATES.LIST && canEdit && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        Nuevo
                    </Button>
                )
            }
        >
            {viewState === VIEW_STATES.LIST ? renderListView() : renderFormView()}
        </Drawer>
    );
};

export default BlacklistDrawer;
